/**
 * Client-side mirror of the EpisodeSchema rules (Memory spec 02 §9.9.3).
 * The server-side validator is authoritative; this exists to give SMEs fast feedback in the
 * AMD editor before submitting. Rule 8 (registered derivers) is server-only knowledge and
 * is deliberately not checked here.
 */

const NAME_PATTERN = /^[a-z][a-zA-Z0-9]*$/;

/** Enum names accepted for field type; numeric wire values also accepted. */
const FIELD_TYPES: Record<string, number> = {
  enum: 1, set: 2, bool: 3, bucket: 4, code: 5, count: 6, text: 7,
};

const FIELD_SOURCES: Record<string, number> = {
  tooltrace: 1, externalmessages: 2, derived: 3,
};

const normalizeType = (value: unknown): number | null => {
  if (typeof value === 'number') return value >= 1 && value <= 7 ? value : null;
  if (typeof value === 'string') return FIELD_TYPES[value.toLowerCase()] ?? null;
  return null;
};

const normalizeSource = (value: unknown): number | null => {
  if (typeof value === 'number') return value >= 1 && value <= 3 ? value : null;
  if (typeof value === 'string') return FIELD_SOURCES[value.toLowerCase()] ?? null;
  return null;
};

/**
 * Validates an episode schema JSON document. Returns a list of violations; empty means valid
 * as far as the client can tell.
 */
export function validateEpisodeSchemaJson(
  json: string,
  scopeDimensionKeys: string[]
): string[] {
  const errors: string[] = [];

  let parsed: any;
  try {
    parsed = JSON.parse(json);
  } catch (e: any) {
    return [`Not valid JSON: ${e.message}`];
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return ['The schema must be a JSON object with schemaVersion and fields.'];
  }

  const schemaVersion = parsed.schemaVersion ?? parsed.SchemaVersion;
  if (typeof schemaVersion !== 'number' || schemaVersion < 1) {
    errors.push('schemaVersion must be a number >= 1.');
  }

  const fields = parsed.fields ?? parsed.Fields;
  if (!Array.isArray(fields) || fields.length === 0) {
    errors.push('The schema must declare at least one field.');
    return errors;
  }

  const names = new Set<string>();
  let discriminatingCount = 0;
  let hasDiscriminatingToolTrace = false;

  for (const raw of fields) {
    const name: string = raw.name ?? raw.Name ?? '';
    const label = name || '(unnamed)';

    // Rule 9 — names unique, camelCase, identifier-safe.
    if (!NAME_PATTERN.test(name)) {
      errors.push(`Rule 9: field name '${label}' must match [a-z][a-zA-Z0-9]*.`);
    }
    if (names.has(name)) {
      errors.push(`Rule 9: duplicate field name '${label}'.`);
    }
    names.add(name);

    const type = normalizeType(raw.type ?? raw.Type);
    if (type === null) {
      errors.push(`Field '${label}' has an unknown type — use Enum, Set, Bool, Bucket, Code, Count or Text.`);
    }

    const source = normalizeSource(raw.source ?? raw.Source);
    if (source === null) {
      errors.push(`Field '${label}' has an unknown source — use ToolTrace, ExternalMessages or Derived.`);
    }

    const description = raw.description ?? raw.Description;
    if (typeof description !== 'string' || description.trim() === '') {
      errors.push(`Field '${label}' needs a description — it is the extraction instruction.`);
    }

    const discriminating = raw.discriminating ?? raw.Discriminating ?? false;
    if (discriminating) {
      discriminatingCount++;
      if (source === 1) hasDiscriminatingToolTrace = true;
      // Rule 2 — Text (7) and Count (6) may never discriminate.
      if (type === 6 || type === 7) {
        errors.push(`Rule 2: field '${label}' (Text/Count) may not be discriminating.`);
      }
    }

    // Rule 3 — Enum/Set carry 2–20 allowed values.
    if (type === 1 || type === 2) {
      const allowed = raw.allowedValues ?? raw.AllowedValues;
      const count = Array.isArray(allowed) ? allowed.length : 0;
      if (count < 2 || count > 20) {
        errors.push(`Rule 3: field '${label}' must carry 2–20 allowedValues; found ${count}.`);
      }
    }

    // Rule 4 — Bucket carries 2–6 ascending bounds.
    if (type === 4) {
      const bounds = raw.bucketBounds ?? raw.BucketBounds;
      if (!Array.isArray(bounds) || bounds.length < 2 || bounds.length > 6) {
        errors.push(`Rule 4: field '${label}' must carry 2–6 bucketBounds.`);
      } else {
        for (let i = 1; i < bounds.length; i++) {
          if (bounds[i] <= bounds[i - 1]) {
            errors.push(`Rule 4: field '${label}' bucketBounds must be strictly ascending.`);
            break;
          }
        }
      }
    }

    // Rule 5 — Code names a CodeSystem.
    if (type === 5) {
      const codeSystem = raw.codeSystem ?? raw.CodeSystem;
      if (typeof codeSystem !== 'string' || codeSystem.trim() === '') {
        errors.push(`Rule 5: Code field '${label}' must name a codeSystem.`);
      }
    }

    // Rule 6 — no collision with scope dimension keys (UserId is always reserved).
    const reserved = [...scopeDimensionKeys, 'UserId'];
    if (reserved.some((k) => k.toLowerCase() === name.toLowerCase())) {
      errors.push(`Rule 6: field '${label}' collides with a ScopeDimension key.`);
    }
  }

  // Rule 1 — at most 8 discriminating fields.
  if (discriminatingCount > 8) {
    errors.push(`Rule 1: at most 8 discriminating fields; found ${discriminatingCount}.`);
  }

  // Rule 7 — at least one discriminating ToolTrace field.
  if (!hasDiscriminatingToolTrace) {
    errors.push(
      'Rule 7: at least one discriminating field must have source ToolTrace — the discriminating ' +
      'variable in workflow failure is what was done, not what was said.'
    );
  }

  return errors;
}

/** The default per-AMD episode schema (spec 02 §9.9.3 rule 7 example), pretty-printed. */
export const DEFAULT_EPISODE_SCHEMA_JSON = JSON.stringify(
  {
    schemaVersion: 1,
    fields: [
      {
        name: 'toolOutcomeClass',
        type: 'Enum',
        source: 'ToolTrace',
        description:
          'Classify the final tool interaction of the session: Succeeded if the last consequential ' +
          'tool call completed normally, Rejected if an external system refused it, Errored if it ' +
          'failed technically.',
        discriminating: true,
        required: true,
        allowedValues: ['Succeeded', 'Rejected', 'Errored'],
      },
      {
        name: 'retryOccurred',
        type: 'Bool',
        source: 'ToolTrace',
        description: 'True if any tool call was retried after a failure or rejection.',
        discriminating: true,
        required: true,
      },
      {
        name: 'sessionNarrative',
        type: 'Text',
        source: 'ExternalMessages',
        description:
          'One or two sentences describing what was attempted and how it concluded, without names, ' +
          'identifiers or dates.',
        required: false,
      },
    ],
  },
  null,
  2
);
